/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { createRole } from '../../../services/users/rolesApi';
import { notifyApiError } from '../../../utils/feedback';
import {
   Section,
   SectionHeader,
   SectionTitle,
   SectionBody,
} from '../../../styles/layout/Section';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field } from '../../../styles/forms/Field';
import { GhostButton, PrimaryButton } from '../../../styles/ui/Button';

// ---------------------------------------------------------------------------
// Modo criação (`/roles/new`) — o protótipo do redesenho não cobre criação,
// então o fluxo segue o padrão das telas vizinhas de IAM (CreateUser →
// ficha): form curto de nome + descrição, cria com ZERO permissões e navega
// para a tela de gestão, onde a matriz é o lugar natural de conceder.
// Vocabulário Section/FormGrid/Field mantido por coerência com o módulo
// (decisão registrada em docs/architecture/users §6).
// ---------------------------------------------------------------------------

const FootRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 16px;
`;

const InlineError = styled.small`
   display: block;
   margin-top: 4px;
   font-size: 12px;
   color: ${({ theme }) => theme.colors.danger};
`;

export default function CreateRoleForm({ onCancel, onCreated }) {
   const [name, setName] = useState('');
   const [description, setDescription] = useState('');
   const [saving, setSaving] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});

   const handleSubmit = async () => {
      const trimmed = name.trim();
      if (trimmed.length < 2) {
         setFieldErrors({ name: 'O nome deve ter pelo menos 2 caracteres.' });
         return;
      }
      setFieldErrors({});
      setSaving(true);
      try {
         const { id } = await createRole({ name: trimmed, description });
         toast.success('Perfil de acesso criado. Agora conceda as permissões.');
         onCreated(id);
      } catch (err) {
         if (err?.fieldErrors && Object.keys(err.fieldErrors).length) {
            setFieldErrors(err.fieldErrors);
         } else {
            notifyApiError(err);
         }
      } finally {
         setSaving(false);
      }
   };

   return (
      <Section>
         <SectionHeader>
            <SectionTitle>Novo perfil de acesso</SectionTitle>
         </SectionHeader>
         <SectionBody>
            <FormGrid $cols={12} $colsMobile={12}>
               <Field $col={5} $colMobile={12}>
                  <label>Nome *</label>
                  <input
                     type="text"
                     value={name}
                     onChange={(e) => setName(e.target.value)}
                     placeholder="Ex.: Supervisor de loja"
                     maxLength={80}
                     autoFocus
                  />
                  {fieldErrors.name && (
                     <InlineError>{fieldErrors.name}</InlineError>
                  )}
               </Field>
               <Field $col={7} $colMobile={12}>
                  <label>Descrição</label>
                  <input
                     type="text"
                     value={description}
                     onChange={(e) => setDescription(e.target.value)}
                     placeholder="Descreva as responsabilidades deste perfil"
                     maxLength={255}
                  />
                  {fieldErrors.description && (
                     <InlineError>{fieldErrors.description}</InlineError>
                  )}
               </Field>
            </FormGrid>

            <FootRow>
               <GhostButton type="button" onClick={onCancel} disabled={saving}>
                  Cancelar
               </GhostButton>
               <PrimaryButton
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving || !name.trim()}
               >
                  {saving ? 'Criando...' : 'Criar perfil'}
               </PrimaryButton>
            </FootRow>
         </SectionBody>
      </Section>
   );
}

CreateRoleForm.propTypes = {
   onCancel: PropTypes.func.isRequired,
   onCreated: PropTypes.func.isRequired,
};
